import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UpdoxContact } from '../updox-contact';
import { UpdoxMessagesService } from '../updox-messages.service';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';

@Component({
  selector: 'app-updox-contacts',
  templateUrl: './contacts.component.html',
})
export class UpdoxContactsComponent implements OnInit {
  @Input() practiceId: number;
  @Output() composeToContact = new EventEmitter<UpdoxContact>();

  contacts: UpdoxContact[] = [];
  isLoading = false;
  showForm = false;
  isEditing = false;
  isSaving = false;
  newContact = new UpdoxContact();
  editingContact: UpdoxContact = null;
  searchTerm = '';

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.loadContacts();
  }

  // Returns the contact bound to the form — either the new draft or the one being edited.
  get formContact(): UpdoxContact {
    return this.isEditing ? this.editingContact : this.newContact;
  }

  loadContacts(): void {
    this.isLoading = true;
    this.messagesService.getContacts(this.practiceId)
      .then((resp: any[]) => {
        this.isLoading = false;
        this.contacts = (resp || []).map(c => new UpdoxContact().load_from_json(c));
      })
      .catch(() => {
        this.isLoading = false;
        this.contacts = [];
      });
  }

  openAddForm(): void {
    this.newContact = new UpdoxContact();
    this.newContact.practice_id = this.practiceId;
    this.isEditing = false;
    this.editingContact = null;
    this.showForm = true;
  }

  openEditForm(contact: UpdoxContact): void {
    // Deep-copy so cancelling restores the original values in the list.
    this.editingContact = new UpdoxContact().load_from_json({
      id: contact.id,
      practice_id: contact.practice_id,
      name: contact.name,
      direct_address: contact.direct_address,
      organization: contact.organization,
      specialty: contact.specialty,
      auto_added: contact.auto_added,
      created_at: contact.created_at,
    });
    this.isEditing = true;
    this.showForm = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.isEditing = false;
    this.editingContact = null;
    this.newContact = new UpdoxContact();
  }

  saveContact(): void {
    this.isSaving = true;

    if (this.isEditing) {
      this.messagesService.updateContact(this.editingContact)
        .then(resp => {
          this.isSaving = false;
          this.showForm = false;
          this.isEditing = false;
          const updated = new UpdoxContact().load_from_json(resp);
          const idx = this.contacts.findIndex(c => c.id === updated.id);
          if (idx > -1) { this.contacts[idx] = updated; }
          this.editingContact = null;
          this.alertService.success('Contact updated.', this.alertService.options);
        })
        .catch(resp => {
          this.isSaving = false;
          this.globalErrorHandler.error = resp?.error?.error || 'Failed to update contact. Please try again.';
        });
    } else {
      this.messagesService.createContact(this.newContact)
        .then(resp => {
          this.isSaving = false;
          this.showForm = false;
          const contact = new UpdoxContact().load_from_json(resp);
          this.contacts.unshift(contact);
          this.alertService.success('Contact added.', this.alertService.options);
        })
        .catch(resp => {
          this.isSaving = false;
          this.globalErrorHandler.error = resp?.error?.error || 'Failed to save contact. Please try again.';
        });
    }
  }

  deleteContact(contact: UpdoxContact): void {
    this.messagesService.deleteContact(contact.id)
      .then(() => {
        this.contacts = this.contacts.filter(c => c.id !== contact.id);
        // If this contact was open in the edit form, close it.
        if (this.isEditing && this.editingContact?.id === contact.id) {
          this.cancelForm();
        }
        this.alertService.success('Contact removed.', this.alertService.options);
      })
      .catch(() => {
        this.globalErrorHandler.error = 'Failed to delete contact. Please try again.';
      });
  }

  get filteredContacts(): UpdoxContact[] {
    if (!this.searchTerm.trim()) { return this.contacts; }
    const term = this.searchTerm.toLowerCase();
    return this.contacts.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.direct_address.toLowerCase().includes(term) ||
      (c.organization || '').toLowerCase().includes(term)
    );
  }
}
